import {
  Button,
  ConfirmDialog,
  FlashMessage,
  Modal,
  ReadMoreLess,
} from "../../common";
import React, { useEffect, useRef, useState } from "react";
import MiniPlus from "../icons/plus-circle-pink.svg";
import VerticalDots from "../icons/dots-red.svg";
import SendIcon from "../icons/send-icon.svg";
// import ArrowRight from "../icons/arrow-right-pink.svg";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  clearAllRequest,
  searchAllRequest,
} from "../../../reducers/SearchAndConnect/searchAndConnect.reducer";
import SearchCandidate from "./SearchCandidate";
import RecommendationType from "./RecommendationType";
import Image from "../../common/Image";
import { Switch } from "@material-ui/core";
import { convertUTCToLocal } from "../../../utils/helper";
import _ from "lodash";
import { rootReducersState } from "../../../reducers";
import candidateServices from "../../../services/candidate.services";
import {
  getPrefillWriteTextFromAskRequest,
  getUserDisplayJobTitle,
} from "../../../utils/helper";
import { ASK_REQUEST_PLACEHOLDER_TEXTS } from "../../../utils/helper";

const normalizeRecommendationsData = (data: any) => {
  if (!data) return null;

  if (_.isArray(data)) {
    return {
      receiver: { received: data, pending: [], revisePending: [] },
      sender: { given: [], pending: [], revisePending: [] },
    };
  }

  if (_.isObject(data) && (_.get(data, "receiver") || _.get(data, "sender"))) {
    return {
      receiver: {
        received: _.get(data, "receiver.received", []),
        pending: _.get(data, "receiver.pending", []),
        revisePending: _.get(data, "receiver.revisePending", []),
      },
      sender: {
        given: _.get(data, "sender.given", []),
        pending: _.get(data, "sender.pending", []),
        revisePending: _.get(data, "sender.revisePending", []),
      },
    };
  }

  const publicReceivedList =
    _.get(data, "received", null) ||
    _.get(data, "public", null) ||
    _.get(data, "list", null);

  if (_.isArray(publicReceivedList)) {
    return {
      receiver: { received: publicReceivedList, pending: [], revisePending: [] },
      sender: { given: [], pending: [], revisePending: [] },
    };
  }

  return null;
};

/** Public visitors: hide pending/declined. Received = profile owner opted in; Given = both sides. */
const isPublicFlagOn = (v: unknown) =>
  v === true || v === 1 || v === "1";

const passesPublicVisibility = (rec: any, tab: "received" | "given") => {
  const st = String(rec?.status ?? "");
  if (st === "1") return false;
  if (st === "2") return false;
  if (tab === "received") {
    return isPublicFlagOn(rec?.is_public_received);
  }
  return (
    isPublicFlagOn(rec?.is_public_given) &&
    isPublicFlagOn(rec?.is_public_received)
  );
};

const filterRecommendationsForPublicVisitor = (
  items: any[],
  tab: string,
  isPublicPage: boolean,
  isOwnProfile: boolean
) => {
  if (!isPublicPage || isOwnProfile) return items;
  if (tab === "pending") return [];
  if (tab !== "received" && tab !== "given") return items;
  return items.filter((rec) => passesPublicVisibility(rec, tab));
};

const getCardDisplayUser = (
  recommendation: any,
  activeTab: string,
  profileCandidateId: number | string | null | undefined
) => {
  if (activeTab === "given") return recommendation?.receiver;
  if (activeTab === "received") return recommendation?.sender;
  if (activeTab === "pending") {
    const pid = profileCandidateId;
    if (
      pid != null &&
      recommendation?.sender_id != null &&
      recommendation?.receiver_id != null
    ) {
      if (Number(recommendation.sender_id) === Number(pid)) {
        return recommendation.receiver;
      }
      if (Number(recommendation.receiver_id) === Number(pid)) {
        return recommendation.sender;
      }
    }
    return recommendation?.sender || recommendation?.receiver;
  }
  return recommendation?.sender;
};

/** Whether the recommendation body has been written (Jenny submitted text). */
const hasRecommendationBody = (rec: any) =>
  !!(rec?.recommendation_text && String(rec.recommendation_text).trim());

/** Ask rows store a placeholder until B writes — treat as no real body for badges/UI. */
const isRequestPlaceholderBody = (rec: any) => {
  const t = String(rec?.recommendation_text || "").trim();
  if (!t) return true;
  return ASK_REQUEST_PLACEHOLDER_TEXTS.test(t);
};

const hasRealRecommendationBody = (rec: any) =>
  hasRecommendationBody(rec) && !isRequestPlaceholderBody(rec);

/** Matches backend: main_status PENDING=2, GIVEN=1; status REQUESTED=1 */
const MAIN_PENDING = "2";
const MAIN_GIVEN = "1";
const STATUS_REQUESTED = "1";

/**
 * Pending row kinds (Ask + Write flows — see product spec).
 * - sent: A is waiting for B to write (is_requested + main pending)
 * - request: B must write or decline
 * - received_pending: A must accept or decline B’s text before it appears on Received with Add to profile
 */
type PendingRowKind =
  | "sent"
  | "request"
  | "received_pending"
  | "revise_sent"
  | "revise_request"
  | "other";

const getPendingRowKind = (
  rec: any,
  sessionUserId: string | number | undefined
): PendingRowKind => {
  if (!sessionUserId) return "other";
  const uid = Number(sessionUserId);
  const isSender = Number(rec?.sender_id) === uid;
  const isReceiver = Number(rec?.receiver_id) === uid;
  const fromRevise = rec?._fromReviseBucket === true;
  const reqFlag =
    rec?.is_requested === true || rec?.is_requested === 1;
  const main = String(rec?.main_status ?? "");
  const st = String(rec?.status ?? "");

  if (!fromRevise && reqFlag && main === MAIN_PENDING) {
    if (isSender) return "sent";
    if (isReceiver) return "request";
  }

  if (fromRevise) {
    if (isSender) return "revise_sent";
    if (isReceiver) return "revise_request";
    return "other";
  }

  if (
    isReceiver &&
    hasRealRecommendationBody(rec) &&
    !reqFlag &&
    st === STATUS_REQUESTED &&
    main === MAIN_GIVEN
  ) {
    return "received_pending";
  }

  return "other";
};

const getPendingBadgeText = (kind: PendingRowKind) => {
  switch (kind) {
    case "sent":
      return "Sent";
    case "request":
      return "Request";
    /** Revise: A (receiver) waiting → Sent; B (writer) must act → Request */
    case "revise_sent":
      return "Request";
    case "revise_request":
      return "Sent";
    case "received_pending":
      return "Received";
    default:
      return "Pending";
  }
};

const getPendingBadgeClassName = (kind: PendingRowKind) => {
  switch (kind) {
    case "sent":
    case "revise_request":
      return "recommendation-badge recommendation-badge--sent";
    case "request":
    case "revise_sent":
      return "recommendation-badge recommendation-badge--request";
    case "received_pending":
      return "recommendation-badge recommendation-badge--received";
    default:
      return "recommendation-badge recommendation-badge--neutral";
  }
};

/** No ⋮: outgoing ask (Sent), or profile owner waiting on revise (revise_request). Writer (revise_sent) has ⋮. */
const shouldShowPendingKebabMenu = (kind: PendingRowKind) =>
  kind !== "sent" && kind !== "revise_request";

/** Other party for “request an edit” (profile owner asks recommender to edit). */
const getOtherPartyForEditRequest = (
  rec: any,
  sessionUserId: string | number | undefined
) => {
  if (!rec || !sessionUserId) return rec?.receiver;
  if (Number(rec.receiver_id) === Number(sessionUserId)) return rec.sender;
  return rec.receiver;
};

/** Prefill edit modal: show proposed revise body when present, else published text. */
const getTextForEditRecommendation = (rec: any): string => {
  const revised = rec?.revise_recommdation_text;
  if (revised != null && String(revised).trim() !== "") {
    return String(revised);
  }
  return String(rec?.recommendation_text ?? "");
};

/** Relationship line for edit/revise: requested type from profile owner when a revise is open. */
const getRelationshipForEditRecommendation = (rec: any): string => {
  const rev = rec?.revise_relationship;
  if (rev != null && String(rev).trim() !== "") {
    return String(rev);
  }
  return String(rec?.relationship ?? "");
};

export type ProfileOwnerSummary = {
  id?: number | string;
  first_name?: string;
  last_name?: string;
  profile_image?: string;
};

function RecommendationsSection({
  isPublicPage,
  isOwnProfile = false,
  profileCandidateId,
  profileOwner,
  initialRecommendations,
}: {
  isPublicPage?: boolean;
  /** True when the logged-in user is the candidate whose profile is shown. */
  isOwnProfile?: boolean;
  profileCandidateId?: number | string | null;
  /** Profile being viewed (used for “Write a recommendation” on public profiles). */
  profileOwner?: ProfileOwnerSummary | null;
  initialRecommendations?: any;
}) {
  const hideVisitorActions = Boolean(isPublicPage && !isOwnProfile);
  const useEmbeddedRecommendationsOnly = Boolean(isPublicPage && !isOwnProfile);
  const sessionUser = useSelector(
    ({ session }: rootReducersState) => session.currentUser
  );
  const sessionUserId = _.get(sessionUser, "id", "") as
    | string
    | number
    | undefined;
  const [recommendationModal, setRecommendationModal] = useState(false);
  const [recommendationModalMode, setRecommendationModalMode] = useState<
    "ask" | "write"
  >("ask");
  const [activeTab, setActiveTab] = useState<string>("received");
  const [editRecommendation, setEditRecommendation] = useState<boolean>(false);
  const [recommendationValue, setRecommendationValue] = useState<string>("");
  const [candidateName, setCandidateName] = useState<string>("");
  const [selectedCandidate, setSelectedCandidate] = useState<any>(null);
  const [getRecommendations, setGetRecommendations] = useState<any>(null);
  const [selectedRecommendation, setSelectedRecommendation] =
    useState<any>(null);
  const [editRecommendationText, setEditRecommendationText] = useState("");

  const [candidateError, setCandidateError] = useState("");
  const [recommendationTypeError, setRecommendationTypeError] = useState("");

  const [showRecommendationDropdown, setShowRecommendationDropdown] =
    useState<any>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const hasFetchedRecommendationsRef = useRef(false);
  /** Public visitor: switch off default “Received” once if that tab is empty but “Given” has mutual-public items. */
  const publicVisitorTabDefaultedRef = useRef(false);

  const [confirmDeleteRecommendation, setConfirmDeleteRecommendation] =
    useState<boolean>(false);
  const [requestEditModal, setRequestEditModal] = useState<boolean>(false);
  const [pendingWriteModal, setPendingWriteModal] = useState(false);
  const [pendingWriteTarget, setPendingWriteTarget] = useState<any>(null);
  const [pendingWriteText, setPendingWriteText] = useState("");
  const [pendingWriteRelationship, setPendingWriteRelationship] =
    useState<string>("");
  const [pendingWriteTypeError, setPendingWriteTypeError] = useState("");
  const [pendingWriteLoading, setPendingWriteLoading] = useState(false);
  const pendingWriteSenderJobTitle = pendingWriteTarget
    ? getUserDisplayJobTitle(pendingWriteTarget.sender)
    : "";
  const [declineConfirmOpen, setDeclineConfirmOpen] = useState(false);
  const [declineTarget, setDeclineTarget] = useState<any>(null);

  const [searchTerm, setSearchTerm] = useState<string>("");
  const dispatch = useDispatch();

  // debounce logic inside parent
  useEffect(() => {
    if (searchTerm === "") return;

    const debounce = setTimeout(() => {
      dispatch(
        searchAllRequest({
          q: searchTerm,
          type: "user",
          page: 1,
          allResult: true,
          pageSize: 20,
        })
      );
    }, 1000);
    return () => clearTimeout(debounce);
  }, [searchTerm, dispatch]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  const {
    search: { loading, currentData },
  } = useSelector(({ search }: rootReducersState) => search);

  const fetchRecommendations = async () => {
    if (useEmbeddedRecommendationsOnly) {
      const normalizedRecommendations =
        normalizeRecommendationsData(initialRecommendations);
      setGetRecommendations(
        normalizedRecommendations || {
          receiver: {},
          sender: {},
        }
      );
      return;
    }

    try {
      const response: any = await candidateServices.getRecommendations();
      if (response?.flag) {
        setGetRecommendations(response.data);
      }
    } catch (error) {
      console.warn("Error fetching recommendations", "error");
    }
  };

  useEffect(() => {
    if (useEmbeddedRecommendationsOnly) {
      fetchRecommendations();
      return;
    }
    if (hasFetchedRecommendationsRef.current) return;
    hasFetchedRecommendationsRef.current = true;
    fetchRecommendations();
    // eslint-disable-next-line
  }, [useEmbeddedRecommendationsOnly, initialRecommendations]);

  useEffect(() => {
    publicVisitorTabDefaultedRef.current = false;
  }, [initialRecommendations]);

  useEffect(() => {
    if (!hideVisitorActions) return;
    setActiveTab((t) => (t === "pending" ? "received" : t));
  }, [hideVisitorActions]);

  useEffect(() => {
    if (!hideVisitorActions || !getRecommendations) return;
    if (publicVisitorTabDefaultedRef.current) return;
    const recv = filterRecommendationsForPublicVisitor(
      [...(getRecommendations?.receiver?.received || [])],
      "received",
      true,
      false
    );
    const given = filterRecommendationsForPublicVisitor(
      getRecommendations?.sender?.given || [],
      "given",
      true,
      false
    );
    if (recv.length === 0 && given.length > 0) {
      setActiveTab("given");
    }
    publicVisitorTabDefaultedRef.current = true;
  }, [hideVisitorActions, getRecommendations]);

  useEffect(() => {
    return () => {
      dispatch(clearAllRequest());
    };
  }, [dispatch]);

  const openAskRecommendationModal = () => {
    setRecommendationModalMode("ask");
    setRecommendationValue("");
    setRecommendationTypeError("");
    setCandidateError("");
    setSearchTerm("");
    setSelectedCandidate(null);
    setCandidateName("");
    reset();
    setRecommendationModal(true);
  };

  const openWriteRecommendationModal = () => {
    const uid = _.get(sessionUser, "id", "");
    if (!uid) {
      FlashMessage("Please log in to write a recommendation", "error");
      return;
    }
    if (
      profileCandidateId != null &&
      Number(uid) === Number(profileCandidateId)
    ) {
      FlashMessage("You cannot write a recommendation for yourself", "error");
      return;
    }
    const ownerId = profileOwner?.id ?? profileCandidateId;
    if (ownerId == null || ownerId === "") {
      FlashMessage("Profile is not available", "error");
      return;
    }
    const name = `${profileOwner?.first_name ?? ""} ${profileOwner?.last_name ?? ""}`.trim();
    setRecommendationModalMode("write");
    setRecommendationValue("");
    setRecommendationTypeError("");
    setCandidateError("");
    setSearchTerm("");
    setSelectedCandidate({
      id: ownerId,
      first_name: profileOwner?.first_name,
      last_name: profileOwner?.last_name,
      profile_image: profileOwner?.profile_image,
    });
    setCandidateName(name || "Candidate");
    reset();
    setRecommendationModal(true);
  };

  const onSubmit = async (data: any) => {
    setCandidateError("");
    setRecommendationTypeError("");

    const receiverId =
      recommendationModalMode === "write"
        ? profileOwner?.id ?? profileCandidateId
        : selectedCandidate?.id;

    if (!receiverId) {
      setCandidateError(
        recommendationModalMode === "write"
          ? "Profile is not available"
          : "Please select a candidate"
      );
      return;
    }
    if (!recommendationValue?.trim()) {
      setRecommendationTypeError("Please select a recommendation type");
      return;
    }

    if (
      recommendationModalMode === "write" &&
      !(data.recommendation_description || "").trim()
    ) {
      FlashMessage("Please write your recommendation", "error");
      return;
    }

    const payload = {
      receiver_id: receiverId,
      relationship: recommendationValue,
      recommendation_text: (data.recommendation_description || "").trim(),
    };

    try {
      const response =
        recommendationModalMode === "ask"
          ? await candidateServices.requestRecommendation(payload)
          : await candidateServices.postRecommendations(payload);
      if (response?.flag) {
        FlashMessage(
          recommendationModalMode === "ask"
            ? "Recommendation request sent successfully"
            : "Recommendation sent successfully"
        );
        setRecommendationModal(false);
        setRecommendationModalMode("ask");
        reset();
        setSelectedCandidate(null);
        setCandidateName("");
        setRecommendationValue("");
        fetchRecommendations();
      } else {
        FlashMessage("Failed to send recommendation", "error");
      }
    } catch (error) {
      FlashMessage("Error sending recommendation", "error");
    }
  };

  const getTabData = () => {
    if (!getRecommendations) return [];

    switch (activeTab) {
      case "received":
        return [...(getRecommendations?.receiver?.received || [])];
      case "given":
        return getRecommendations?.sender?.given || [];
      case "pending":
        return [
          ...(getRecommendations?.sender?.pending || []).map((r: any) => ({
            ...r,
            _pendingBucket: "sender",
          })),
          ...(getRecommendations?.receiver?.pending || []).map((r: any) => ({
            ...r,
            _pendingBucket: "receiver",
          })),
          ...(getRecommendations?.sender?.revisePending || []).map((r: any) => ({
            ...r,
            _pendingBucket: "sender",
            _fromReviseBucket: true,
          })),
          ...(getRecommendations?.receiver?.revisePending || []).map((r: any) => ({
            ...r,
            _pendingBucket: "receiver",
            _fromReviseBucket: true,
          })),
        ];
      default:
        return [];
    }
  };

  const getDisplayTabData = () => {
    const raw = getTabData();
    return filterRecommendationsForPublicVisitor(
      raw,
      activeTab,
      Boolean(isPublicPage),
      Boolean(isOwnProfile)
    );
  };

  const confirmDeleteSubmit = async () => {
    if (!selectedRecommendation)
      return FlashMessage("Please select a recommendation to delete", "error");

    try {
      const response = await candidateServices.deleteRecommendations(
        selectedRecommendation.id
      );
      if (response?.flag) {
        FlashMessage("Recommendation deleted successfully");
        setConfirmDeleteRecommendation(false);
        setSelectedRecommendation(null);
        fetchRecommendations();
      } else {
        FlashMessage("Failed to delete recommendation", "error");
      }
    } catch (error) {
      FlashMessage("Error deleting recommendation", "error");
    }
  };

  const submitEditRecommendation = async () => {
    if (!selectedRecommendation || !editRecommendationText) return;
    const payload = {
      recommendation_text: editRecommendationText,
      relationship: recommendationValue
        ? recommendationValue
        : selectedRecommendation?.relationship,
      flag: false,
    };
    try {
      const response = await candidateServices.updateRecommendations(
        selectedRecommendation.id,
        payload
      );
      if (response?.flag) {
        FlashMessage("Recommendation updated successfully");
        setEditRecommendation(false);
        setSelectedRecommendation(null);
        setEditRecommendationText("");
        setRecommendationValue("");
        fetchRecommendations();
      } else {
        FlashMessage("Failed to send edit request", "error");
      }
    } catch (error) {
      FlashMessage("Error sending edit request", "error");
    }
  };

  const markPublicRecommendation = async (
    flag: boolean,
    recommendation: any
  ) => {
    try {
      if (!recommendation?.id) {
        FlashMessage("No recommendation selected", "error");
        return;
      }
      const payload = {
        recommendation_id: recommendation.id,
        flag: flag, // Assuming true means approved
      };
      const result = await candidateServices.markPublicRecommendations(payload);
      if (result?.flag) {
        FlashMessage(
          `Recommendation set to ${flag ? "public" : "private"} successfully`
        );
        setShowRecommendationDropdown(null);
        fetchRecommendations();
      } else {
        FlashMessage(result?.message ?? "Failed to update", "error");
      }
    } catch (error) {
      console.log({ error });
      FlashMessage("Something went wrong!", "error");
    }
  };

  const approveRecommendation = async (
    recommendation: any,
    status: boolean
  ) => {
    try {
      if (!recommendation) {
        FlashMessage("No recommendation selected", "error");
        return;
      }

      const payload = {
        recommendation_id: recommendation?.id,
        flag: status,
      };
      const result = await candidateServices.approveRecommendations(payload);

      if (result.flag) {
        FlashMessage(
          `Recommendation ${status ? "approved" : "declined"} successfully`
        );
        setShowRecommendationDropdown(null);
        setSelectedRecommendation(null); // Reset the current recommendation
        fetchRecommendations();
      } else {
        FlashMessage(result.message, "error");
      }
    } catch (error) {
      FlashMessage("Something went wrong!", "error");
    }
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowRecommendationDropdown(null);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const submitRequestEditRecommendation = async () => {
    if (!selectedRecommendation || !editRecommendationText) return;
    if (!recommendationValue?.trim()) {
      setRecommendationTypeError("Please select a recommendation type");
      FlashMessage("Please select how you know this person", "error");
      return;
    }
    setRecommendationTypeError("");
    const payload = {
      recommendation_id: selectedRecommendation.id,
      revise_recommdation_text: editRecommendationText,
      relationship: recommendationValue.trim(),
    };
    try {
      const response = await candidateServices.requestUpdateRecommendations(
        payload
      );
      if (response?.flag) {
        FlashMessage("Recommendation revise request sent successfully");
        setRequestEditModal(false);
        setSelectedRecommendation(null);
        setEditRecommendationText("");
        setRecommendationValue("");
        setRecommendationTypeError("");
        fetchRecommendations();
      } else {
        FlashMessage("Failed to send revise request", "error");
      }
    } catch (error) {
      FlashMessage("Error sending revise request", "error");
    }
  };

  const openPendingWriteModal = (rec: any) => {
    setShowRecommendationDropdown(null);
    setPendingWriteTarget(rec);
    setPendingWriteText(getPrefillWriteTextFromAskRequest(rec));
    setPendingWriteRelationship(String(rec?.relationship || "").trim());
    setPendingWriteTypeError("");
    setPendingWriteModal(true);
  };


  const handleCloseRecommendationModal = () => {
    setEditRecommendationText("");
    setRecommendationModal(false);
    setRecommendationModalMode("ask");
    setCandidateError("");
    setRecommendationTypeError("");
    setRecommendationValue("");
    setSelectedCandidate(null);
    setCandidateName("");
  }

  const handleCloseRequestEditRecommendationModal = () => {
    setSelectedRecommendation(null);
    setRequestEditModal(false);
    setEditRecommendationText("");
    setRecommendationValue("");
    setRecommendationTypeError("");
  }

  const handleCloseEditRecommendationModal = () => {
    setSelectedRecommendation(null);
    setEditRecommendation(false);
    setRecommendationValue("");
    setSelectedCandidate(null);
  }
  const submitPendingWriteResponse = async () => {
    if (!pendingWriteTarget) return;
    if (!pendingWriteRelationship?.trim()) {
      setPendingWriteTypeError("Please select a recommendation type");
      FlashMessage("Please select how you know this person", "error");
      return;
    }
    setPendingWriteTypeError("");
    if (!pendingWriteText.trim()) {
      FlashMessage("Please write your recommendation", "error");
      return;
    }
    setPendingWriteLoading(true);
    try {
      const response = await candidateServices.updateRecommendations(
        pendingWriteTarget.id,
        {
          recommendation_text: pendingWriteText.trim(),
          relationship: pendingWriteRelationship.trim(),
          flag: false,
        }
      );
      if (response?.flag) {
        FlashMessage("Recommendation sent successfully");
        setPendingWriteModal(false);
        setPendingWriteTarget(null);
        setPendingWriteText("");
        setPendingWriteRelationship("");
        fetchRecommendations();
      } else {
        FlashMessage(response?.message || "Failed to send recommendation", "error");
      }
    } catch {
      FlashMessage("Error sending recommendation", "error");
    } finally {
      setPendingWriteLoading(false);
    }
  };

  const openDeclineConfirm = (rec: any) => {
    setShowRecommendationDropdown(null);
    setDeclineTarget(rec);
    setDeclineConfirmOpen(true);
  };

  const confirmDeclineRecommendation = async () => {
    if (!declineTarget) return;
    const kind = getPendingRowKind(declineTarget, sessionUserId);
    try {
      if (kind === "request") {
        const result = await candidateServices.respondRecommendationRequest({
          recommendation_id: declineTarget.id,
          action: "decline",
        });
        if (result?.flag) {
          FlashMessage("Recommendation request declined successfully");
          fetchRecommendations();
        } else {
          FlashMessage(result?.message || "Could not decline request", "error");
        }
      } else if (kind === "revise_sent" || kind === "revise_request") {
        const result = await candidateServices.declineReviseRecommendation({
          recommendationId: declineTarget.id,
          flag: false,
          receiver_id: declineTarget.receiver_id,
          recommendation_text: "",
        });
        if (result?.flag) {
          FlashMessage("Update declined successfully");
          fetchRecommendations();
        } else {
          FlashMessage(result?.message || "Could not update", "error");
        }
      } else {
        await approveRecommendation(declineTarget, false);
      }
    } catch {
      FlashMessage("Something went wrong!", "error");
    }
    setDeclineConfirmOpen(false);
    setDeclineTarget(null);
  };

  const openReviseEditFromPending = (rec: any) => {
    console.log(rec);
    setShowRecommendationDropdown(null);
    setEditRecommendation(true);
    setEditRecommendationText(getTextForEditRecommendation(rec));
    setSelectedRecommendation(rec);
    const other = rec?.receiver;
    setCandidateName(
      `${other?.first_name || ""} ${other?.last_name || ""}`.trim()
    );
    setRecommendationValue(getRelationshipForEditRecommendation(rec));
  };

  return (
    <div className="accordion">
      <div className="accordion-head recommendation-accordion-head">
        <b>Recommendations</b>

        {!hideVisitorActions && (
          <div
            className="ask-recommendation"
            onClick={openAskRecommendationModal}
          >
            <img src={MiniPlus} alt="plus" />
            <span className="separator">|</span>
            <span> Ask a recommendation</span>
          </div>
        )}
        {hideVisitorActions && (
          <div
            className="ask-recommendation"
            onClick={openWriteRecommendationModal}
          >
            <img src={MiniPlus} alt="plus" />
            <span className="separator">|</span>
            <span> Write a recommendation</span>
          </div>
        )}
      </div>

      <div className="accordion-body">
        <div className="recommendations-content">
          <div className="recommendation-tabs-wrapper" style={{ gridTemplateColumns: hideVisitorActions ? "1fr 1fr" : "1fr 1fr 1fr" }}>
            {(hideVisitorActions
              ? ["Received", "Given"]
              : ["Received", "Given", "Pending"]
            ).map((tab, index) => (
              <div
                key={index}
                className={`recommendation-tab ${activeTab === tab.toLowerCase() ? "active" : ""
                  } `}
                onClick={() => setActiveTab(tab.toLowerCase())}
              >
                <span>{tab}</span>
              </div>
            ))}
          </div>
          {getDisplayTabData()?.length > 0 ? (
            <>
              <div className="recommendation-card-wrapper">
                {getDisplayTabData()?.map((recommendation: any, index: number) => {
                  const user = getCardDisplayUser(
                    recommendation,
                    activeTab,
                    profileCandidateId
                  );
                  const userJobTitle = getUserDisplayJobTitle(user);
                  const pendingKind: PendingRowKind =
                    activeTab === "pending"
                      ? getPendingRowKind(recommendation, sessionUserId)
                      : "other";
                  let showKebab = !hideVisitorActions;
                  if (activeTab === "pending") {
                    showKebab =
                      showKebab && shouldShowPendingKebabMenu(pendingKind);
                  }
                  return (
                    <div
                      key={recommendation?.id ?? `rec-${index}`}
                      className="recommendation-item"
                    >
                      <div className="card-head">
                        <div className="user-info-wrapper">
                          <div className="user-icon">
                            <Image src={user?.profile_image || ""} alt="user" />
                          </div>
                          <div className="user-info">
                            <h5>
                              {user?.first_name} {user?.last_name}

                              {activeTab === "pending" && (
                                <span
                                  className={getPendingBadgeClassName(
                                    pendingKind
                                  )}
                                >
                                  {getPendingBadgeText(pendingKind)}
                                </span>
                              )}
                              {activeTab === "given" &&
                                _.get(recommendation, "status", "1") ===
                                "1" && (
                                  <span className="pending-status">
                                    Pending
                                  </span>
                                )}

                              {activeTab === "given" &&
                                _.get(recommendation, "status", "1") === "2" && (
                                  <span className="declined-status">
                                    Declined
                                  </span>
                                )}
                            </h5>
                            <div className="recommendation-user-job-title">
                              {userJobTitle ? (
                                <span>
                                  {userJobTitle}
                                </span>
                              ) : null}
                              <span className="recommendation-created-at">
                                {convertUTCToLocal(recommendation?.created_at)}
                              </span>
                            </div>
                          </div>
                        </div>
                        {showKebab ? (
                          <div
                            ref={
                              showRecommendationDropdown === index
                                ? dropdownRef
                                : null
                            }
                            className="dots-icon"
                            onClick={() => {
                              setShowRecommendationDropdown(
                                showRecommendationDropdown === index
                                  ? null
                                  : index
                              );
                            }}
                          >
                            <img src={VerticalDots} alt="dots" />
                            {showRecommendationDropdown === index && (
                              <div className="dropdown-menu">
                                <ul>
                                  {activeTab === "received" && !hideVisitorActions && (
                                    <>
                                      <li>
                                        Public
                                        <Switch
                                          checked={Boolean(
                                            recommendation?.is_public_received
                                          )}
                                          onChange={(e) =>
                                            markPublicRecommendation(
                                              e.target.checked,
                                              recommendation
                                            )
                                          }
                                          inputProps={{
                                            "aria-label": "Public on profile",
                                          }}
                                        />
                                      </li>
                                      <li
                                        onClick={() => {
                                          setShowRecommendationDropdown(null);
                                          setRecommendationTypeError("");
                                          setRequestEditModal(true);
                                          setSelectedRecommendation(
                                            recommendation
                                          );
                                          setEditRecommendationText(
                                            getTextForEditRecommendation(
                                              recommendation
                                            )
                                          );
                                          setRecommendationValue(
                                            getRelationshipForEditRecommendation(
                                              recommendation
                                            )
                                          );
                                          setCandidateName(
                                            `${recommendation?.receiver?.first_name || ""} ${recommendation?.receiver?.last_name || ""}`.trim()
                                          );
                                        }}
                                      >
                                        Request an edit
                                      </li>
                                    </>
                                  )}
                                  {activeTab === "given" && !hideVisitorActions && (
                                    <>
                                      <li>
                                        Public
                                        <Switch
                                          checked={Boolean(
                                            recommendation?.is_public_given
                                          )}
                                          onChange={(e) =>
                                            markPublicRecommendation(
                                              e.target.checked,
                                              recommendation
                                            )
                                          }
                                          inputProps={{
                                            "aria-label": "Public recommendation",
                                          }}
                                        />
                                      </li>
                                      <li
                                        onClick={() => {
                                          setEditRecommendation(true);
                                          setShowRecommendationDropdown(null);
                                          setEditRecommendationText(
                                            getTextForEditRecommendation(
                                              recommendation
                                            )
                                          );
                                          setSelectedRecommendation(
                                            recommendation
                                          );
                                          setCandidateName(
                                            `${recommendation?.receiver?.first_name} ${recommendation?.receiver?.last_name}`
                                          );
                                          setRecommendationValue(
                                            getRelationshipForEditRecommendation(
                                              recommendation
                                            )
                                          );
                                        }}
                                      >
                                        Edit recommendation
                                      </li>
                                      <li
                                        onClick={() => {
                                          setShowRecommendationDropdown(null);
                                          setConfirmDeleteRecommendation(true);
                                          setSelectedRecommendation(
                                            recommendation
                                          );
                                        }}
                                      >
                                        Delete
                                      </li>
                                    </>
                                  )}
                                  {activeTab === "pending" &&
                                    !hideVisitorActions &&
                                    pendingKind === "request" && (
                                      <>
                                        <li
                                          onClick={() => {
                                            setShowRecommendationDropdown(null);
                                            openPendingWriteModal(recommendation);
                                          }}
                                        >
                                          Write recommendation
                                        </li>
                                        <li
                                          onClick={() => {
                                            setShowRecommendationDropdown(null);
                                            openDeclineConfirm(recommendation);
                                          }}
                                        >
                                          Decline
                                        </li>
                                      </>
                                    )}
                                  {activeTab === "pending" &&
                                    !hideVisitorActions &&
                                    pendingKind === "revise_sent" && (
                                      <>
                                        <li
                                          onClick={() => {
                                            setShowRecommendationDropdown(null);
                                            openReviseEditFromPending(
                                              recommendation
                                            );
                                          }}
                                        >
                                          Edit recommendation
                                        </li>
                                        <li
                                          onClick={() => {
                                            setShowRecommendationDropdown(null);
                                            openDeclineConfirm(recommendation);
                                          }}
                                        >
                                          Decline
                                        </li>
                                      </>
                                    )}
                                  {activeTab === "pending" &&
                                    !hideVisitorActions &&
                                    pendingKind === "received_pending" && (
                                      <>
                                        <li
                                          onClick={() => {
                                            setShowRecommendationDropdown(null);
                                            approveRecommendation(
                                              recommendation,
                                              true
                                            );
                                          }}
                                        >
                                          Add to profile
                                        </li>
                                        <li
                                          onClick={() => {
                                            setShowRecommendationDropdown(null);
                                            setRecommendationTypeError("");
                                            setRequestEditModal(true);
                                            setSelectedRecommendation(
                                              recommendation
                                            );
                                            setEditRecommendationText(
                                              getTextForEditRecommendation(
                                                recommendation
                                              )
                                            );
                                            setRecommendationValue(
                                              getRelationshipForEditRecommendation(
                                                recommendation
                                              )
                                            );
                                            setCandidateName(
                                              `${recommendation?.receiver?.first_name || ""} ${recommendation?.receiver?.last_name || ""}`.trim()
                                            );
                                          }}
                                        >
                                          Request an edit
                                        </li>
                                        <li
                                          onClick={() => {
                                            setShowRecommendationDropdown(null);
                                            openDeclineConfirm(recommendation);
                                          }}
                                        >
                                          Decline
                                        </li>
                                      </>
                                    )}
                                  {activeTab === "pending" &&
                                    !hideVisitorActions &&
                                    pendingKind === "other" && (
                                      <>
                                        <li
                                          onClick={() =>
                                            approveRecommendation(
                                              recommendation,
                                              true
                                            )
                                          }
                                        >
                                          Approve
                                        </li>
                                        <li
                                          onClick={() =>
                                            openDeclineConfirm(recommendation)
                                          }
                                        >
                                          Decline
                                        </li>
                                      </>
                                    )}
                                </ul>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="dots-icon dots-icon--placeholder" aria-hidden />
                        )}
                      </div>
                      <div className="about-client">
                        <span>
                          {getRelationshipForEditRecommendation(recommendation)}
                        </span>

                      </div>
                      <div className="bordered-content">
                        {recommendation?.revise_status === 1 &&
                          recommendation?.revise_recommdation_text ? (
                          <ReadMoreLess
                            text={recommendation?.revise_recommdation_text}
                          />
                        ) : hasRealRecommendationBody(recommendation) ? (
                          <ReadMoreLess
                            text={recommendation?.recommendation_text}
                          />
                        ) : activeTab === "pending" ? (
                          <ReadMoreLess
                            text={'Waiting for a recommendation…'}
                          />
                        ) : (
                          <ReadMoreLess text={recommendation?.recommendation_text || ""} />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* <div className="btn-all-wrapper">
                <Button variant="outlined">
                  <span>View All</span>
                  <img src={ArrowRight} alt="Arrow" />
                </Button>
              </div> */}
            </>
          ) : null}
        </div>
      </div>

      <Modal
        className="candidate-profile-modal recommendation-modal"
        isOpen={recommendationModal}
        onClose={() => {
          handleCloseRecommendationModal();
        }}
        title={
          recommendationModalMode === "write"
            ? "Write a Recommendation"
            : "Request a Recommendation"
        }
        visible={recommendationModal}
        size="large"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="modal-body-wrapper">
          <div className="form-wrapper">
            {!_.isEmpty(candidateName) && !_.isEmpty(selectedCandidate) ? (
              <div className="selected-candidate-wrapper">
                <div className="selected-candidate-card">
                  <div className="candidate-img">
                    <Image src={selectedCandidate?.profile_image} alt="icon" />
                  </div>
                  <h6>{candidateName}</h6>
                </div>
                {recommendationModalMode === "ask" && (
                  <div
                    className="change-candidate"
                    onClick={() => {
                      setSearchTerm("");
                      setCandidateName("");
                      setSelectedCandidate(null);
                      setRecommendationValue("");
                      setCandidateError("");
                    }}
                  >
                    Change Candidate
                  </div>
                )}
              </div>
            ) : (
              <div>
                <SearchCandidate
                  label=""
                  placeholder="Which colleague would you like to endorse?"
                  value={candidateName}
                  onChangeName={(name) => {
                    setSearchTerm(name);
                    setCandidateName(name);
                  }}
                  searchData={currentData}
                  onSelect={(candidate) => {
                    setSelectedCandidate(candidate);
                    setCandidateName(
                      `${candidate.first_name} ${candidate.last_name}`
                    );
                    setRecommendationValue("");
                    setCandidateError("");
                  }}
                />
                {candidateError && (
                  <span className="error-msg" style={{ color: "red", display: "block", marginTop: 4 }}>
                    {candidateError}
                  </span>
                )}</div>
            )}


            <RecommendationType
              label={`How do you know ${candidateName || "this colleague"}?`}
              placeholder="Select recommendation type"
              value={recommendationValue}
              onChange={(val) => {
                setRecommendationValue(val);
                setRecommendationTypeError("");
              }}
              errors={errors}
              candidateName={candidateName}
              register={register}
              errorMessage={recommendationTypeError}
            />
            <div className="form-group">
              <label
                htmlFor="recommendation_description"
                className="form-label"
              >
                {recommendationModalMode === "write"
                  ? "Write a recommendation*"
                  : "Optional message"}
              </label>
              <textarea
                placeholder={
                  recommendationModalMode === "write"
                    ? "Write your message here..."
                    : "Add an optional note with your request…"
                }
                className="form-control"
                rows={4}
                {...register("recommendation_description", {
                  maxLength: {
                    value: 1000,
                    message: "Cannot exceed more than 1000 characters",
                  },
                })}
              ></textarea>
              {errors.recommendation_description && (
                <span className="error-msg" style={{ color: "red", display: "block", marginTop: 4 }}>
                  {String(errors.recommendation_description.message ?? "")}
                </span>
              )}
            </div>
          </div>
          <div className="btn-wrapper">
            <div className="send-recommendation-btn">
              <Button variant="outlined" color="secondary" onClick={handleCloseRecommendationModal} className="outlined-btn">Cancel</Button>
              <Button type="submit" loading={loading}>
                <span>Send</span>
              </Button>
            </div>
          </div>
        </form>
      </Modal>
      <Modal
        className="candidate-profile-modal recommendation-modal"
        isOpen={editRecommendation}
        onClose={() => {
          handleCloseEditRecommendationModal();
        }}
        title="Edit a Recommendation"
        visible={editRecommendation}
        size="large"
      >
        <div className="modal-body-wrapper">
          <div className="form-wrapper">
            <div className="profile-wrapper">
              <div className="profile-content">
                <div className="profile-icon">
                  <Image
                    src={selectedRecommendation?.receiver?.profile_image || ""}
                    alt="profile"
                    width="100%"
                  />
                </div>
                <b>
                  {selectedRecommendation?.receiver?.first_name +
                    " " +
                    selectedRecommendation?.receiver?.last_name}
                </b>
              </div>
            </div>
            <RecommendationType
              label="Recommendation Type*"
              placeholder="Select Recommendation"
              value={recommendationValue}
              onChange={(val) => setRecommendationValue(val)}
              errors={errors}
              candidateName={candidateName}
              register={register}
            />
            <div className="form-group">
              <label
                htmlFor="recommendation_description"
                className="form-label"
              >
                Edit recommendation
              </label>
              <textarea
                id="recommendation_description"
                placeholder="Write your message here..."
                className="form-control"
                rows={4}
                value={editRecommendationText || ""}
                onChange={(e) => setEditRecommendationText(e.target.value)}
              ></textarea>
            </div>
          </div>
          <div className="btn-wrapper">
            <div className="send-recommendation-btn">
              <Button variant="outlined" color="secondary" onClick={handleCloseEditRecommendationModal} className="outlined-btn">Cancel</Button>
              <Button onClick={submitEditRecommendation}>
                <span>Send</span>
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        className="candidate-profile-modal recommendation-modal"
        isOpen={requestEditModal}
        onClose={() => {

          handleCloseRequestEditRecommendationModal();
        }}
        title={`Ask ${
          getOtherPartyForEditRequest(selectedRecommendation, sessionUserId)
            ?.first_name ?? ""
        } to update`}
        visible={requestEditModal}
        size="large"
      >
        <div className="modal-body-wrapper">
          <div className="form-wrapper">
            <div className="profile-wrapper">
              <div className="profile-content">
                <div className="profile-icon">
                  <Image
                    src={
                      getOtherPartyForEditRequest(
                        selectedRecommendation,
                        sessionUserId
                      )?.profile_image || ""
                    }
                    alt="profile"
                    width="100%"
                  />
                </div>
                <b>
                  {getOtherPartyForEditRequest(selectedRecommendation, sessionUserId)
                    ?.first_name}{" "}
                  {getOtherPartyForEditRequest(selectedRecommendation, sessionUserId)
                    ?.last_name}
                </b>
              </div>
            </div>
            <RecommendationType
              label="Recommendation Type*"
              placeholder="Select Recommendation"
              value={recommendationValue}
              onChange={(val) => {
                setRecommendationTypeError("");
                setRecommendationValue(val);
              }}
              errors={errors}
              candidateName={candidateName}
              register={register}
              errorMessage={recommendationTypeError}
            />
            <div className="form-group">
              <label
                htmlFor="request_edit_recommendation_description"
                className="form-label"
              >
                Request to edit recommendation
              </label>
              <textarea
                id="request_edit_recommendation_description"
                placeholder="Write your message here..."
                className="form-control"
                rows={4}
                value={editRecommendationText || ""}
                onChange={(e) => setEditRecommendationText(e.target.value)}
              ></textarea>
            </div>
          </div>
          <div className="btn-wrapper">
            <div className="send-recommendation-btn">
              <Button variant="outlined" color="secondary" onClick={handleCloseRequestEditRecommendationModal} className="outlined-btn">Cancel</Button>
              <Button onClick={submitRequestEditRecommendation}>
                <span>Send</span>

              </Button>
            </div>
          </div>
        </div>
      </Modal>
      <Modal
        className="candidate-profile-modal recommendation-modal"
        isOpen={pendingWriteModal}
        onClose={() => {
          setPendingWriteModal(false);
          setPendingWriteTarget(null);
          setPendingWriteText("");
          setPendingWriteRelationship("");
          setPendingWriteTypeError("");
        }}
        title="Write recommendation"
        visible={pendingWriteModal}
        size="large"
      >
        <div className="modal-body-wrapper">
          <div className="form-wrapper">
            {pendingWriteTarget && (
              <div className="profile-wrapper">
                <div className="profile-content">
                  <div className="profile-icon">
                    <Image
                      src={pendingWriteTarget?.sender?.profile_image || ""}
                      alt="profile"
                      width="100%"
                    />
                  </div>
                  <b>
                    {pendingWriteTarget?.sender?.first_name}{" "}
                    {pendingWriteTarget?.sender?.last_name}
                    {pendingWriteSenderJobTitle ? (
                      <span className="recommendation-user-job-title">
                        {" "}
                        {pendingWriteSenderJobTitle}
                      </span>
                    ) : null}
                  </b>
                </div>
              </div>
            )}
            <RecommendationType
              label="How do you know each other?*"
              placeholder="Select recommendation type"
              value={pendingWriteRelationship}
              onChange={(val) => {
                setPendingWriteRelationship(val);
                setPendingWriteTypeError("");
              }}
              errors={errors}
              candidateName={
                pendingWriteTarget?.sender
                  ? `${pendingWriteTarget.sender.first_name || ""} ${pendingWriteTarget.sender.last_name || ""}`.trim()
                  : candidateName || "Candidate"
              }
              register={register}
              errorMessage={pendingWriteTypeError}
            />
            <div className="form-group">
              <label className="form-label" htmlFor="pending-write-text">
                Write a recommendation*
              </label>
              <textarea
                id="pending-write-text"
                className="form-control"
                rows={6}
                value={pendingWriteText}
                onChange={(e) => setPendingWriteText(e.target.value)}
                placeholder="Write your recommendation here…"
              />
            </div>
          </div>
          <div className="btn-wrapper">
            <div className="send-recommendation-btn">
              <Button
                onClick={submitPendingWriteResponse}
                loading={pendingWriteLoading}
              >
                <span>Send</span>
                <div className="icon">
                  <img src={SendIcon} alt="icon" />
                </div>
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        visible={declineConfirmOpen}
        title="Decline recommendation?"
        bodyText="Are you sure you want to decline this recommendation?"
        onCancel={() => {
          setDeclineConfirmOpen(false);
          setDeclineTarget(null);
        }}
        onConfirm={confirmDeclineRecommendation}
      />

      <ConfirmDialog
        visible={confirmDeleteRecommendation}
        title="Delete Recommendation"
        bodyText="Are you sure you want to delete this recommendation?"
        onCancel={() => setConfirmDeleteRecommendation(false)}
        onConfirm={confirmDeleteSubmit}
      />
    </div>
  );
}

export default RecommendationsSection;
